import { AddressWalklist } from '../models/addressWalklist';
import { Observable, of } from 'rxjs';
import { map, tap, switchMap, take, catchError } from 'rxjs/operators';

import { environment } from 'env/config';

import {
  IActivityAddress,
  IAddressWalklist,
  IBulkAction,
} from '../interfaces/models';
import {
  PageConfig,
  Endpoint,
  CentralApis,
  ACTIONS,
} from '../interfaces/internal';
import { PageTitles } from '../interfaces/types';
import { Page } from './page';

import { ActivityAddress } from '../models/activityAddress';
import { Result, IPage } from '../interfaces/helpers';
import { WalklistLDPS } from '../models/walklist';
import { ID2CGeoServiceAddress } from '../models/d2cgeo.model';

export class ActivityAddressPage extends Page<ActivityAddress> {
  private addressWalklist;

  constructor(
    { endpoint }: Endpoint,
    config: PageConfig,
    addressWalklist: IPage<IAddressWalklist>
  ) {
    const url = (endpoint as CentralApis).activity + 'address';

    super(url, PageTitles.activityAddress, config);
    this.addressWalklist = addressWalklist;

    this.initializeColumns({
      keys: new ActivityAddress(),
      hiddenTableFields: ['walklistAssigned', 'walklistId'],
    });
  }

  get actions(): string[] {
    return [ACTIONS.delete];
  }

  get hiddenTableFields(): string[] {
    return ['language'];
  }

  getWalklistSavedAdresses(queryParams?): Observable<Result<WalklistLDPS>> {
    const result: Result<WalklistLDPS> = {
      items: [],
      links: null,
      meta: null,
    };

    return this.getItems(queryParams).pipe(
      take(1),
      switchMap((res: Result<ActivityAddress>) => {
        result.links = res.links;
        result.meta = res.meta;

        if (res.items.length > 0) {
          return this.addressWalklist.fetchByIds({
            ids: res.items.map((i) => `${i.addressId}`).join(','),
            sortBy: environment.app.sort.addressWalklist.join(','),
          });
        } else {
          return of(result);
        }
      }),
      take(1),
      map((addresses: Result<AddressWalklist>) => {
        result.items = addresses.items.map(
          (address) =>
            new WalklistLDPS({
              serviceAddress: address.serviceAddress,
              lpds: address.id.toString(),
              id: address.id,
              premiseType: address.premiseType,
              fsa: address.fsa,
            })
        );

        return result;
      })
    );
  }

  fetch(queryString?): Observable<Result<ActivityAddress>> {
    let result: Result<ActivityAddress>;
    return this.getItems(queryString).pipe(
      take(1),
      switchMap((res: Result<ActivityAddress>) => {
        result = res;
        if (res.items.length > 0) {
          return this.addressWalklist
            .fetchByIds({
              ids: res.items.map((i) => `${i.addressId}`).join(','),
              sortBy: environment.app.sort.addressWalklist.join(','),
            })
            .pipe(map(({ items }) => items));
        } else {
          return of([]);
        }
      }),
      take(1),
      switchMap((addresses: AddressWalklist[]) => {
        result.items = result.items.map((record) => {
          const address = addresses.find(
            (addressWalklist) => addressWalklist.id === record.addressId
          );
          return new ActivityAddress({
            ...record,
            id: record.addressId,
            name: record.addressId,
            salesPremiseType: address ? address.premiseType : '',
            address: address ? address.searchStreet : '',
          });
        });

        return of(result);
      })
    );
  }

  fetchById(id: string): Observable<ActivityAddress> {
    return this.getItem(id).pipe(
      map((data: IActivityAddress) => new ActivityAddress({ ...data, id }))
    );
  }

  update(item: IActivityAddress): Observable<ActivityAddress> {
    let updatedActivityAddress: IActivityAddress[];
    return this.items.pipe(
      take(1),
      switchMap((activityAddress) => {
        if (!activityAddress || activityAddress.length <= 0) {
          return this.fetch().pipe(map(({ items }) => items));
        } else {
          return of(activityAddress);
        }
      }),
      switchMap((activityAddress) => {
        const activityAddressIndex = activityAddress.findIndex(
          (i) => i.id === item.id
        );
        updatedActivityAddress = [...activityAddress];
        const oldActivityAddress = updatedActivityAddress[activityAddressIndex];
        updatedActivityAddress[activityAddressIndex] = new ActivityAddress({
          ...item,
          id: oldActivityAddress.id,
        });
        return this.updateItems(updatedActivityAddress[activityAddressIndex]);
      }),
      tap(() => {
        this.pageItems.next(updatedActivityAddress);
      })
    );
  }

  bulkSave(items): Observable<IActivityAddress[]> {
    const addresses: IBulkAction<ID2CGeoServiceAddress>[] = [];
    items.forEach((i) => {
      addresses.push({
        op: 'add',
        path: '/',
        value: i,
      } as IBulkAction<ID2CGeoServiceAddress>);
    });

    return this.addBulk(addresses, 'assignaddress');
  }

  createFromDTO(item): ActivityAddress {
    return new ActivityAddress(item);
  }

  fecthByPost(params: any) {
    const originalURL = this.url;
    this.url = this.url + '/walklist?limit=10000000';

    return this.getItemsPost(params).pipe(
      tap(() => {
        this.url = originalURL;
      }),
      catchError((err) => {
        this.url = originalURL;
        return null;
      })
    );
  }
}
